import { Link } from "react-router";
import type { EventDTO, GitBranchSnapshot, TaskDTO } from "@roomate/core";
import { useAbandonmentEvents, useEvents, useGitSnapshot, useProject } from "./api.ts";
import { abandonmentFor, type AbandonmentView } from "./abandon-merge.tsx";
import { cx, Empty, ErrorNote, Section } from "./ui.tsx";

/**
 * Self-contained git state panels for task #13 — not mounted into Bridge.tsx, App.tsx or
 * styles.css (frozen for the in-flight redesign). Each component fetches its own data from
 * `projectId`, so wiring either into a new layout is a one-line `<Component projectId={id} />`.
 *
 * - `GitBranchesPanel`: the full per-branch table (AC1) — mounted on the project page here.
 * - `GitSummary`: the compact "N ready · N conflict · N merged today" line (AC2) — mounted on
 *   the project page too (so it's e2e-verifiable now) and meant for the Bridge Code panel once
 *   the redesign lands; see the evidence note for the exact mount point.
 */

function startOfLocalDay(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Combines the git snapshot's own `merged`/`conflicts` with the task's merge-queue outcome. */
export function branchStatus(branch: GitBranchSnapshot, task: TaskDTO | undefined, abandonment?: AbandonmentView | null): { label: string; cls: string; title?: string } {
  if (task?.mergeState === "abandoned") return { label: "abandoned", cls: "text-medium", title: abandonment?.reason };
  if (task?.mergeState === "merge_failed") return { label: "merge failed", cls: "text-high" };
  if (branch.merged) return { label: "merged", cls: "text-ok" };
  if (branch.conflicts) return { label: "conflict", cls: "text-high" };
  if (task?.stage === "done") return { label: "queued to merge", cls: "text-medium" };
  return { label: "building", cls: "text-ink-3" };
}

export function queueBranchCounts(branches: readonly GitBranchSnapshot[], tasks: readonly TaskDTO[]): { ready: number; conflicts: number } {
  const taskById = new Map(tasks.map((task) => [task.id, task]));
  // Only branches of live work count: a branch left behind by a task that already merged, was cancelled or was
  // abandoned is history, not a conflict anyone has to resolve. Branches with no task are counted as before.
  const active = branches.filter((branch) => {
    if (!branch.taskId) return true;
    const task = taskById.get(branch.taskId);
    if (!task) return false;
    return task.stage !== "cancelled" && task.mergeState !== "merged" && task.mergeState !== "abandoned";
  });
  return {
    ready: active.filter((branch) => !branch.merged && !branch.conflicts).length,
    conflicts: active.filter((branch) => branch.conflicts).length,
  };
}

export function GitBranchesPanel({ projectId }: { projectId: string }) {
  const git = useGitSnapshot(projectId);
  const project = useProject(projectId);
  const events = useAbandonmentEvents(project.data?.tasks ?? []);

  if (git.isLoading) {
    return (
      <Section title="Git">
        <Empty>Loading…</Empty>
      </Section>
    );
  }
  if (git.isError) {
    return (
      <Section title="Git">
        <ErrorNote error={git.error} />
      </Section>
    );
  }
  const snap = git.data;
  if (!snap) {
    return (
      <Section title="Git">
        <Empty>No git snapshot yet — start a node with this project's checkout.</Empty>
      </Section>
    );
  }
  const tasks = project.data?.tasks ?? [];
  const taskById = new Map(tasks.map((t) => [t.id, t] as const));

  return (
    <Section
      title="Git"
      aside={
        <span className="font-mono">
          main {snap.main.sha ? snap.main.sha.slice(0, 7) : "—"}
          {snap.main.hasOrigin && ` · ${snap.main.ahead}↑ ${snap.main.behind}↓ origin`}
        </span>
      }
    >
      {snap.branches.length === 0 ? (
        <Empty>No agent branches yet — a branch appears here once a task is claimed.</Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-[12.5px]" data-testid="git-branches-table">
            <thead>
              <tr className="border-b border-rule text-left font-mono text-[10.5px] uppercase tracking-wider text-ink-3">
                <th className="py-1.5 pr-3 font-medium">Branch</th>
                <th className="py-1.5 pr-3 font-medium">Ahead / behind</th>
                <th className="py-1.5 pr-3 font-medium">Diff</th>
                <th className="py-1.5 pr-3 font-medium">Last commit</th>
                <th className="py-1.5 pr-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {snap.branches.map((b) => {
                const task = b.taskId ? taskById.get(b.taskId) : undefined;
                const abandonment = task ? abandonmentFor(task, events) : null;
                const status = branchStatus(b, task, abandonment);
                return (
                  <tr key={b.name} className="border-b border-rule/60" data-testid="git-branch-row" data-branch={b.name}>
                    <td className="py-1.5 pr-3 font-mono">
                      {b.taskId ? (
                        <Link to={`/tasks/${b.taskId}`} className="hover:text-ink hover:underline">
                          {b.name}
                        </Link>
                      ) : (
                        b.name
                      )}
                    </td>
                    <td className="py-1.5 pr-3 font-mono text-ink-2">
                      {b.ahead}↑ {b.behind}↓
                    </td>
                    <td className="py-1.5 pr-3 font-mono text-ink-2">
                      {b.filesChanged} file{b.filesChanged === 1 ? "" : "s"} · +{b.insertions} −{b.deletions}
                    </td>
                    <td className="py-1.5 pr-3 text-ink-2">{b.lastCommit ? b.lastCommit.message : "—"}</td>
                    <td className={cx("py-1.5 pr-3 font-mono uppercase tracking-wider", status.cls)} data-testid="git-branch-status" title={status.title}>
                      {status.label}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {snap.worktrees.length > 0 && (
        <p className="mt-3 font-mono text-[11px] text-ink-3" data-testid="git-worktrees">
          worktrees: {snap.worktrees.length} · dirty: {snap.worktrees.filter((w) => w.dirty).length}
        </p>
      )}
    </Section>
  );
}

export function GitSummary({ projectId }: { projectId: string }) {
  const git = useGitSnapshot(projectId);
  const project = useProject(projectId);
  // A merge is dated by the `task.merged` event the merge queue emits, not by any field on the
  // branch snapshot itself — `lastCommit.at` is the branch's last commit, not when it merged.
  // Pull a wide window so an early-morning merge on a busy project doesn't fall off the page;
  // still a cap, not a true fix — a project with 200+ events since midnight would undercount.
  const events = useEvents(projectId, 200);

  if (git.isLoading) return <Empty>Loading…</Empty>;
  if (git.isError) return <ErrorNote error={git.error} />;
  const snap = git.data;
  if (!snap) return <Empty>No git snapshot yet — start a node with this project's checkout.</Empty>;

  const { ready, conflicts } = queueBranchCounts(snap.branches, project.data?.tasks ?? []);
  const since = startOfLocalDay();
  const mergedToday = (events.data ?? []).filter((e: EventDTO) => e.type === "task.merged" && e.ts >= since).length;

  return (
    <p className="font-mono text-[12.5px] text-ink-3" data-testid="git-summary">
      main {snap.main.sha ? snap.main.sha.slice(0, 7) : "—"}
      {snap.main.hasOrigin && ` · ${snap.main.ahead}↑ ${snap.main.behind}↓`}
      {" · "}
      <span data-testid="git-summary-ready">{ready} ready</span>
      {" · "}
      <span className={cx(conflicts > 0 && "font-medium text-high")} data-testid="git-summary-conflicts">
        {conflicts} conflict{conflicts === 1 ? "" : "s"}
      </span>
      {" · "}
      <span data-testid="git-summary-merged-today">{mergedToday} merged today</span>
      {snap.worktrees.length > 0 && (
        <>
          {" · "}
          {snap.worktrees.length} worktree{snap.worktrees.length === 1 ? "" : "s"}
          {snap.worktrees.some((w) => w.dirty) && `, ${snap.worktrees.filter((w) => w.dirty).length} dirty`}
        </>
      )}
    </p>
  );
}
